import { Component } from '@theme/component';

// Only explicitly named characteristics become facets. Unspecified data stays unspecified.
const fields = ['category','metal','stone'];
const specific = { rings:['stone'] };
const metalColors = ['Yellow Gold','Rose Gold','White Gold'];
const metalName = value => {
  const normalized = String(value || '').trim().replace(/\s+/g,' ').replace(/^whitw gold$/i,'white gold');
  return metalColors.find(color=>color.toLowerCase()===normalized.toLowerCase()) || '';
};
const unique = values => [...new Set(values.filter(Boolean))].sort((a,b)=>a.localeCompare(b,undefined,{numeric:true}));
const words = (text, list) => list.filter(word => new RegExp(`\\b${word}\\b`,'i').test(text));
export function measurements(text) {
  const number = String.raw`\d+(?:\.\d+)?`;
  const unit = String.raw`(?:["”″]|inches\b|inch\b|in\b|cm\b|mm\b)`;
  const measure = `${number}(?:\\s*[-–—x×/]\\s*${number})?\\s*${unit}`;
  const pattern = new RegExp(String.raw`\b(?:length|size|wrist size|adjustable length)\s*:\s*(${measure}(?:\s*(?:\+|[-–—])\s*${measure}(?:\s*extension)?)?)`,'gi');
  return unique([...text.matchAll(pattern)].map(m=>m[1].trim()));
}
export function describe(product) {
  const text = `${product.type} ${product.title} ${product.tags.join(' ')}`;
  let category = /earring|\bstuds?\b|\bhoops?\b/i.test(text) ? 'earrings' : /hand\s?chain/i.test(text) ? 'handchains' : /\brings?\b/i.test(text) ? 'rings' : /bracelet|bangle|\bcuff\b/i.test(text) ? 'bracelets' : /necklace/i.test(text) ? 'necklaces' : /pendant|charm/i.test(text) ? 'charms' : '';
  
  return {...product, category, wrist:measurements(product.description), style:words(text,['solitaire','eternity','stacking','signet','cocktail','dome','pavé','pave','tennis','paperclip','rope','cuban','figaro','heart','initial','lariat']),stone:words(text+' '+product.description,['diamond','emerald','ruby','sapphire','pearl','onyx','turquoise','amethyst','topaz','aquamarine','opal','citrine','malachite','agate','labradorite','mother of pearl','lapis lazuli','peridot','garnet','tourmaline','morganite','tanzanite']),earring_type:words(text,['studs','stud','hoops','hoop','drops','drop']).map(x=>x.replace(/s$/,'')),necklace_type:words(text,['chain','pendant']),bracelet_type:words(text,['chain','bangle'])};
}
function option(product,variant,field) {
  const pattern = field==='metal' ? /colou?r|metal/ : /size|length/;
  const index = product.options.findIndex(name=>pattern.test(name.toLowerCase()));
  const value = index < 0 ? '' : variant.options[index];
  return field==='metal' ? metalName(value) : value;
}
function values(product,field) {
  if (field==='category') return [product.category];
  if (['metal','size','wrist'].includes(field)) return unique([...product.variants.map(v=>option(product,v,field)),...(field==='wrist'?product[field]:[])]);
  return product[field] || [];
}
export function matchingVariant(product,state,readyCollection=false) {
  if (state.category && product.category!==state.category) return null;
  for (const field of fields.filter(f=>!['category','metal'].includes(f))) {
    if (state[field] && !values(product,field).includes(state[field])) return null;
  }
  const matches = product.variants.filter(variant => {
    if ((readyCollection || state.ready==='1') && !variant.stocked) return false;
    if (state.min!=='' && state.min!=null && variant.price < Number(state.min)*100) return false;
    if (state.max!=='' && state.max!=null && variant.price > Number(state.max)*100) return false;
    return !state.metal || option(product,variant,'metal')===state.metal;
  });
  return matches.find(v=>v.stocked) || matches.find(v=>v.available) || matches[0] || null;
}
function element(tag,text,className) {
  const node=document.createElement(tag);
  if(text!==undefined) node.textContent=text;
  if(className) node.className=className;
  return node;
}
class KLRCollection extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller=new AbortController();
    const signal=this.controller.signal;
    this.labels=JSON.parse(this.querySelector('[data-labels]').textContent);
    this.readyCollection=this.dataset.ready==='true';
    this.form=this.querySelector('[data-filters]');
    this.status=this.querySelector('[data-status]');
    this.querySelector('[data-retry]').addEventListener('click',()=>this.load(),{signal});
    this.querySelector('[data-apply]').addEventListener('click',()=>{
      this.querySelector('details').open=false;
      this.querySelector('summary').focus();
    },{signal});
    this.form.addEventListener('submit',event=>event.preventDefault(),{signal});
    this.form.addEventListener('change',()=>{
      const next=Object.fromEntries(new FormData(this.form));
      if((next.category||'')!==(this.state.category||'')) {
        for(const field of Object.values(specific).flat()) delete next[field];
      }
      this.state={...next,page:'1'};
      this.syncURL(); this.buildForm(); this.render();
    },{signal});
    window.addEventListener('popstate',()=>{if(this.products){this.readURL();this.buildForm();this.render();}},{signal});
    this.load();
  }
  disconnectedCallback() { super.disconnectedCallback(); this.controller?.abort(); }
  readURL() {
    const params=new URL(location.href).searchParams;
    this.state=Object.fromEntries([...params].filter(([k])=>k.startsWith('klr_') && [...fields,'ready','min','max','sort','page'].includes(k.slice(4))).map(([k,v])=>[k.slice(4),v]));
    if(this.state.metal) this.state.metal=metalName(this.state.metal);
    if(!this.state.sort && params.get('sort_by')==='created-descending') this.state.sort='newest';
  }
  syncURL() {
    const url=new URL(location.href);
    for(const key of [...url.searchParams.keys()]) if(key.startsWith('klr_')||key==='page') url.searchParams.delete(key);
    for(const [key,value] of Object.entries(this.state)) if(value) url.searchParams.set(`klr_${key}`,value);
    history.pushState(null,'',url);
  }
  async load() {
    if(this.loading) return;
    this.loading=true;
    this.dataset.loadState='loading';
    this.status.hidden=false;
    this.status.textContent=this.labels.loading;
    this.querySelector('[data-retry]').hidden=true;
    try {
      const requestPage=async page=> {
        const url=new URL(this.dataset.url,location.origin);
        url.searchParams.set('view','klr-data');url.searchParams.set('page',String(page));
        const original=new URL(location.href);
        for(const key of ['preview_theme_id','sort_by']) if(original.searchParams.has(key)) url.searchParams.set(key,original.searchParams.get(key));
        const response=await fetch(url,{signal:this.controller.signal,credentials:'same-origin'});
        if(!response.ok) throw new Error('Collection request failed');
        const doc=new DOMParser().parseFromString(await response.text(),'text/html');
        const data=JSON.parse(doc.getElementById('klr-data').textContent);
        if(data.page!==page || !Array.isArray(data.products)) throw new Error('Invalid collection response');
        return data;
      };
      const first=await requestPage(1);
      const remaining=await Promise.all(Array.from({length:first.pages-1},(_,i)=>requestPage(i+2)));
      const all=[first,...remaining].flatMap(data=>data.products);
      this.products=[...new Map(all.map(p=>[p.id,describe(p)])).values()];
      this.readURL();this.buildForm();this.render();
      this.querySelector('[data-enhancement]').hidden=false;
      this.dataset.loadState='ready';
      this.querySelector('[data-loading-grid]').hidden=true;
      this.querySelector('[data-loading-filter]').hidden=true;
      const fallback=document.getElementById(this.dataset.fallbackId);
      if(fallback?.tagName==='RESULTS-LIST') fallback.hidden=true;
    } catch(error) {
      if(error.name!=='AbortError') {this.dataset.loadState='failed';this.querySelector('[data-loading-grid]').hidden=true;this.querySelector('[data-loading-filter]').hidden=true;this.status.textContent=this.labels.failed;this.querySelector('[data-retry]').hidden=false;}
    } finally {this.loading=false;}
  }
  label(field,value) { return field==='category' ? this.labels[value]||value : value; }
  buildForm() {
    const focused=this.form.contains(document.activeElement) ? document.activeElement.name : null;
    this.form.replaceChildren();
    const scope=this.products.filter(p=>!this.state.category || p.category===this.state.category);
    const categories=unique(this.products.map(p=>p.category));
    const category=this.state.category || (categories.length===1?categories[0]:'');
    for(const field of ['category','metal',...(specific[category]||[])]) {
      const label=element('label',this.labels[field]);const select=element('select');select.name=field;select.setAttribute('aria-label',this.labels[field]);
      select.append(new Option(this.labels.all_values,''));
      const choices=field==='metal' ? metalColors : unique((field==='category'?this.products:scope).flatMap(p=>values(p,field)));
      for(const value of choices) select.append(new Option(this.label(field,value),value));
      if(this.state[field] && ![...select.options].some(o=>o.value===this.state[field])) select.append(new Option(this.state[field],this.state[field]));
      select.value=this.state[field]||'';
      label.append(select);this.form.append(label);
    }
    for(const field of ['min','max']) {
      const label=element('label',`${this.labels[field==='min'?'minimum':'maximum']} (${this.dataset.currency})`);const input=element('input');
      input.name=field;input.setAttribute('aria-label',this.labels[field==='min'?'minimum':'maximum']);input.type='number';input.min='0';input.step='any';input.inputMode='decimal';input.value=this.state[field]||'';
      label.append(input);this.form.append(label);
    }
    const readyLabel=element('label',this.labels.ready_only,'klr-checkbox');const ready=element('input');ready.type='checkbox';ready.name='ready';ready.value='1';ready.checked=this.readyCollection||this.state.ready==='1';ready.disabled=this.readyCollection;readyLabel.prepend(ready);this.form.append(readyLabel);
    const sortLabel=element('label',this.labels.sort);const sort=element('select');sort.name='sort';sort.setAttribute('aria-label',this.labels.sort);
    for(const value of ['featured','price_low','price_high','newest']) sort.append(new Option(this.labels[value],value));
    sort.value=this.state.sort||'featured';sortLabel.append(sort);this.form.append(sortLabel);
    if(focused) [...this.form.elements].find(e=>e.name===focused)?.focus();
  }
  render() {
    const results=this.products.map(p=>({p,v:matchingVariant(p,this.state,this.readyCollection)})).filter(x=>x.v);
    if(this.state.sort==='price_low') results.sort((a,b)=>a.v.price-b.v.price);
    if(this.state.sort==='price_high') results.sort((a,b)=>b.v.price-a.v.price);
    if(this.state.sort==='newest') results.sort((a,b)=>Number(b.p.created)-Number(a.p.created));
    const count=results.length,pages=Math.max(1,Math.ceil(count/24)),page=Math.min(pages,Math.max(1,parseInt(this.state.page)||1));
    this.status.textContent=count ? '' : this.labels.none;
    this.status.hidden=count>0;
    const grid=this.querySelector('[data-grid]');grid.replaceChildren();
    for(const {p,v} of results.slice((page-1)*24,page*24)) {
      const li=element('li'),link=element('a',undefined,'klr-card');const url=new URL(p.url,location.origin);url.searchParams.set('variant',v.id);link.href=url.pathname+url.search;
      if(v.image||p.image) {const img=element('img');img.src=v.image||p.image;img.alt=p.title;img.width=800;img.height=800;img.loading='lazy';link.append(img);}
      link.append(element('span',p.title,'klr-card-title'),element('span',v.money));
      const label=p.gift ? this.labels.digital : v.stocked ? (v.title==='Default Title'?this.labels.ready:`${this.labels.stocked_in} ${v.title}`) : v.available?this.labels.made:this.labels.unavailable;
      link.append(element('span',label,'klr-card-stock'));li.append(link);grid.append(li);
    }
    const chips=this.querySelector('[data-chips]');chips.replaceChildren();
    for(const [key,value] of Object.entries(this.state)) {
      if(!value || ['page','sort'].includes(key) || ![...fields,'ready','min','max'].includes(key))continue;
      const label=key==='ready'?this.labels.ready_only:`${this.labels[key==='min'?'minimum':key==='max'?'maximum':key]}: ${this.label(key,value)}`;
      const button=element('button',`${label} ×`);button.type='button';button.setAttribute('aria-label',`${this.labels.remove} ${label}`);
      button.addEventListener('click',()=>{delete this.state[key];if(key==='category')for(const f of Object.values(specific).flat())delete this.state[f];this.state.page='1';this.syncURL();this.buildForm();this.render();this.querySelector('summary').focus();},{signal:this.controller.signal});chips.append(button);
    }
    if(chips.children.length) {const clear=element('button',this.labels.clear);clear.type='button';clear.addEventListener('click',()=>{this.state={};this.syncURL();this.buildForm();this.render();this.querySelector('summary').focus();},{signal:this.controller.signal});chips.append(clear);}
    const nav=this.querySelector('[data-pagination]');nav.replaceChildren();
    for(const [key,target] of [['previous',page-1],['next',page+1]]) {
      const button=element('button',this.labels[key]);button.type='button';button.disabled=target<1||target>pages;
      button.addEventListener('click',()=>{this.state.page=String(target);this.syncURL();this.render();this.scrollIntoView({block:'start'});this.querySelector('summary').focus();},{signal:this.controller.signal});nav.append(button);
      if(key==='previous')nav.append(element('span',`${this.labels.page} ${page} / ${pages}`));
    }
  }
}
if(!customElements.get('klr-collection'))customElements.define('klr-collection',KLRCollection);
// Prefill only the visitor's unsent inquiry, without submitting or replacing their work.
const query=new URL(location.href).searchParams;
const body=document.querySelector('textarea[name="contact[body]"]');
if(body && !body.value && (query.has('topic')||query.has('piece'))) body.value=[query.get('topic'),query.get('piece')].filter(Boolean).join('\n\n');

// Dock the app's exported launcher in a reserved mobile help row, outside product text.
class KLRMobileChat extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller=new AbortController();
    const signal=this.controller.signal;
    this.update=()=>{
      const host=document.querySelector('shopify-chat');
      if(!host) return;
      const rect=this.querySelector('a').getBoundingClientRect();
      const visible=rect.top>=0 && rect.bottom<=innerHeight && rect.width>0;
      host.setAttribute('data-klr-docked','');
      const button=host.shadowRoot?.querySelector('shopify-agent-runtime')?.shadowRoot?.querySelector('[part="activator"]');
      const parent=button?.parentElement;
      const parentStyle=parent && getComputedStyle(parent);
      const anchored=parentStyle && (parentStyle.translate!=='none' || parentStyle.transform!=='none');
      const origin=anchored?parent.getBoundingClientRect():{top:0,left:0};
      host.style.setProperty('--klr-chat-top',`${rect.top-origin.top}px`);
      host.style.setProperty('--klr-chat-left',`${rect.left-origin.left}px`);
      host.style.setProperty('--klr-chat-visibility',visible?'visible':'hidden');
      this.toggleAttribute('data-chat-ready',Boolean(button));
    };
    this.observer=new MutationObserver(this.update);
    this.observer.observe(document.body,{childList:true});
    this.resizeObserver=new ResizeObserver(this.update);
    this.resizeObserver.observe(this);
    window.addEventListener('scroll',this.update,{signal,passive:true});
    window.addEventListener('resize',this.update,{signal,passive:true});
    this.timer=setInterval(this.update,1000);
    this.update();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();this.observer?.disconnect();this.resizeObserver?.disconnect();clearInterval(this.timer);
    const host=document.querySelector('shopify-chat');host?.removeAttribute('data-klr-docked');
  }
}
if(!customElements.get('klr-mobile-chat'))customElements.define('klr-mobile-chat',KLRMobileChat);
